import { capitalizeFirstLetterOfEachWord } from '.';
import { getFlatTypeSlug } from './flatType.utils';
import { getSearchPageUrlsMap } from './i18n.utils';
import {
  extractCountyCityAndZoneFromQuery,
  slugifySearchQuery,
} from './searchQuery.utils';
import { SEARCH_BY_COUNTY_SUFFIX } from '../consts';
import { IFlat } from '../model/flat.model';
import { FlatType } from '../model/enums/flatType.enum';
import { SearchOptionType } from '../model/enums/searchOptionType.enum';

export type SearchOption = {
  text: string;
  count: number;
  county: string | null;
  city: string | null;
  zone: string | null;
  matched: boolean;
  synonyms?: string[];
};

export const computeSearchOptions = (
  flats: IFlat[],
  includeAllSearchOptions = false
): SearchOption[] => {
  let allSearchOptions = computeAllSearchOptions(flats);
  allSearchOptions = sortSearchOptions(allSearchOptions);

  const urlsMap = getSearchPageUrlsMap();
  let searchOptions: SearchOption[] = [];
  for (const [query, translations] of Object.entries(urlsMap)) {
    const [county, city, zone] = extractCountyCityAndZoneFromQuery(
      query.replaceAll('/', '--')
    );

    let searchOption = findSearchOptionByCountyCityAndZone(
      allSearchOptions,
      county,
      city,
      zone
    );

    if (!searchOption) {
      searchOption = {
        text: 'willBeAssignedLater',
        count: 0,
        county: capitalizeFirstLetterOfEachWord(
          county.replaceAll(`${SEARCH_BY_COUNTY_SUFFIX}`, '')
        ),
        city: city
          ? capitalizeFirstLetterOfEachWord(city.replaceAll('-', ' '))
          : null,
        zone: zone
          ? capitalizeFirstLetterOfEachWord(zone.replaceAll('-', ' '))
          : null,
        matched: true,
      };
    }

    searchOption.text = translations.locationName;
    searchOption.matched = true;
    searchOption.synonyms = translations.synonymSearchOptions
      ? translations.synonymSearchOptions.split(';')
      : undefined;

    searchOptions.push(searchOption);
  }

  if (includeAllSearchOptions) {
    for (const searchOption of allSearchOptions) {
      if (
        !searchOptions.find(
          (o) =>
            o.county === searchOption.county &&
            o.city === searchOption.city &&
            o.zone === searchOption.zone
        )
      ) {
        searchOptions.push(searchOption);
      }
    }
  }

  return sortSearchOptions(searchOptions);
};

export const findSearchOptionByCountyCityAndZone = (
  searchOptions: SearchOption[],
  county: string,
  city: string,
  zone: string
): SearchOption | undefined => {
  return searchOptions.find((option) => {
    if (zone) {
      const zonePart = option.zone
        ? slugifySearchQuery(option.zone)
        : undefined;
      if (zonePart !== zone) {
        return false;
      }
    } else if (option.zone) {
      return false;
    }

    if (city) {
      const cityPart = option.city
        ? slugifySearchQuery(option.city)
        : undefined;
      if (cityPart !== city) {
        return false;
      }
    } else if (option.city) {
      return false;
    }

    const countyPart = option.county
      ? slugifySearchQuery(option.county) + SEARCH_BY_COUNTY_SUFFIX
      : undefined;
    return countyPart === county;
  });
};

export const getSearchOptionType = (
  searchOption: SearchOption
): SearchOptionType | null => {
  if (searchOption.zone) {
    return SearchOptionType.ZONE;
  }
  if (searchOption.city) {
    return SearchOptionType.CITY;
  }
  if (searchOption.county) {
    return SearchOptionType.COUNTY;
  }
  return null;
};

export const filterByText = (searchOptions: SearchOption[], q: string) => {
  if (!q) {
    return searchOptions;
  }

  return searchOptions.filter(
    (option) =>
      option.text.toLowerCase().includes(q.toLowerCase()) ||
      option.synonyms?.some((synonym) =>
        synonym.toLowerCase().includes(q.toLowerCase())
      )
  );
};

export const getCountySearchOptions = (searchOptions: SearchOption[]) =>
  searchOptions.filter(
    (option) =>
      option.county && !option.city && !option.zone && option.count > 0
  );

export const generateUrlFromSearchOption = (
  searchOption: SearchOption,
  includePrefix = false
): string => {
  return `${
    includePrefix
      ? '/comprar/' + getFlatTypeSlug(FlatType.PISO, true) + '/'
      : ''
  }${slugifySearchQuery(searchOption.county) + SEARCH_BY_COUNTY_SUFFIX}${
    searchOption.city ? '/' + slugifySearchQuery(searchOption.city) : ''
  }${searchOption.zone ? '/' + slugifySearchQuery(searchOption.zone) : ''}`;
};

const computeAllSearchOptions = (flats: IFlat[]): SearchOption[] => {
  const countiesSet = new Set<string>();
  const citiesSet = new Set<string>();
  const zonesSet = new Set<string>();
  for (const flat of flats) {
    countiesSet.add(flat.county);
    if (flat.city && flat.county) {
      citiesSet.add(`${flat.city}--${flat.county}`);
    }
    if (flat.zone && flat.city) {
      zonesSet.add(`${flat.zone}--${flat.city}`);
    }
  }

  const searchOptions: SearchOption[] = [];
  for (const county of countiesSet) {
    searchOptions.push({
      text: county,
      count: flats.filter((flat) => flat.county === county).length,
      county,
      city: null,
      zone: null,
      matched: false,
    });
  }
  for (const cityAndCounty of citiesSet) {
    const city = cityAndCounty.split('--')[0];
    const [county, count] = flats.reduce(
      (acc, flat) => {
        if (
          flat.city === city &&
          flat.county === cityAndCounty.split('--')[1]
        ) {
          acc[0] = flat.county;
          acc[1]++;
        }
        return acc;
      },
      ['', 0]
    );
    searchOptions.push({
      text: city,
      count,
      county,
      city,
      zone: null,
      matched: false,
    });
  }
  for (const zoneAndCity of zonesSet) {
    const zone = zoneAndCity.split('--')[0];
    const [county, city, count] = flats.reduce(
      (acc, flat) => {
        if (flat.zone === zone && flat.city === zoneAndCity.split('--')[1]) {
          acc[0] = flat.county;
          acc[1] = flat.city;
          acc[2]++;
        }
        return acc;
      },
      ['', '', 0]
    );
    searchOptions.push({
      text: `${zone} (${city})`,
      count,
      county,
      city,
      zone,
      matched: false,
    });
  }

  return searchOptions;
};

const sortSearchOptions = (searchOptions: SearchOption[]): SearchOption[] => {
  return [
    ...searchOptions.filter((option) => !!option.zone),
    ...searchOptions.filter((option) => !option.zone && !!option.city),
    ...searchOptions.filter(
      (option) => !option.zone && !option.city && !!option.county
    ),
    ...searchOptions.filter(
      (option) => !option.zone && !option.city && !option.county
    ),
  ];
};
